/**
 * The template palette, grouped by role, from the library API (requirement
 * 13.1). Each role is an accordion section, as in draw.io: a small heading
 * that opens and closes a grid of symbols in place, each named in its tooltip
 * and to assistive technology. Several sections can be open at once, and
 * which ones are open is remembered in the browser (a view preference, like
 * the annotation toggles; it never reaches a design). Searching lists every
 * match, open, so a part whose section is not known is still one step away.
 *
 * Drag a tile onto the canvas, or activate it (click, Enter, Space) to place
 * at the centre of the view, or into the selected line: the keyboard path for
 * placing (requirement 12.7). A heading toggles with Enter or Space, and the
 * down arrow from an open heading moves into its grid; the arrow keys move
 * through the grid, and the up arrow from its first row returns to the heading.
 *
 * The palette can be folded away to a strip, and made wider or narrower by
 * dragging its right edge (or, focused, with the arrow keys). Both are
 * remembered in the browser, as view preferences.
 *
 * Blocks come last, in their own section. Placing one is a server operation,
 * so a block tile goes to `onPlaceBlock` rather than `onPlace`, and is never
 * spliced into a line.
 */
import {
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import type { DesignKind } from "../design/document";
import { BLOCK_DRAG_TYPE, TEMPLATE_DRAG_TYPE } from "../editor";
import { Glyph } from "../render/glyphs";
import { BlockThumbnail } from "./BlockThumbnail";
import { PanelTab } from "./PanelTab";
import { paletteBlocks, paletteGroups, type PaletteItem, type WorkspaceLibrary } from "./library";
import { PaneResizer } from "./PaneResizer";
import {
  loadPalettePanel,
  loadPaletteSections,
  PALETTE_WIDTH,
  savePalettePanel,
  savePaletteSections,
  type PalettePanel,
} from "./preferences";

interface Section {
  label: string;
  items: PaletteItem[];
  dragType: string;
  place: (versionId: string) => void;
}

function accessibleName(item: PaletteItem): string {
  const kind = item.role === "block" ? " block" : "";
  return item.projectScoped ? `${item.name}${kind} (project${kind ? "" : " variant"})` : `${item.name}${kind}`;
}

function PaletteTile({
  item,
  dragType,
  onPlace,
  library,
}: {
  item: PaletteItem;
  dragType: string;
  onPlace: (versionId: string) => void;
  /** Given for a block, which is drawn as a thumbnail of what it holds. */
  library?: WorkspaceLibrary;
}) {
  const name = accessibleName(item);
  const definition = item.role === "block" ? library?.scene.definitions[item.versionId] : undefined;
  return (
    <button
      type="button"
      className={`ws-tile${item.projectScoped ? " ws-tile-project" : ""}${definition ? " ws-tile-block" : ""}`}
      title={name}
      aria-label={`Place ${name}`}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData(dragType, item.versionId);
        event.dataTransfer.effectAllowed = "copy";
      }}
      onClick={() => onPlace(item.versionId)}
    >
      {definition && library ? (
        <BlockThumbnail
          definition={definition.content}
          library={library.scene}
          width={BLOCK_THUMBNAIL.width}
          height={BLOCK_THUMBNAIL.height}
        />
      ) : (
        <svg width={34} height={24} viewBox="-16 -11 32 22" aria-hidden="true">
          <Glyph name={item.glyph} size={{ width: 26, height: 18 }} />
        </svg>
      )}
    </button>
  );
}

/** A block's tile: big enough to see what it holds. */
const BLOCK_THUMBNAIL = { width: 96, height: 64 };

/**
 * Tiles per row as laid out: the grid fills the palette's width, so this is
 * read from where the tiles fell rather than fixed. One row where there is no
 * layout (a test's DOM).
 */
function columnsOf(tiles: readonly HTMLElement[]): number {
  const top = tiles[0]?.offsetTop;
  const inFirstRow = tiles.filter((tile) => tile.offsetTop === top).length;
  return Math.max(1, inFirstRow);
}

/**
 * Arrow keys move between tiles in the grid; Home and End go to its ends.
 * Returns false for ArrowUp from the first row, which leaves the grid.
 */
function moveAmongTiles(event: ReactKeyboardEvent<HTMLElement>): boolean {
  const tiles = [...event.currentTarget.querySelectorAll<HTMLButtonElement>(".ws-tile")];
  const columns = columnsOf(tiles);
  const steps: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns };
  const index = tiles.indexOf(document.activeElement as HTMLButtonElement);
  let next: number;
  if (event.key === "Home") next = 0;
  else if (event.key === "End") next = tiles.length - 1;
  else if (event.key in steps) {
    if (event.key === "ArrowUp" && index < columns) return false;
    next = Math.max(0, Math.min(tiles.length - 1, index + steps[event.key]!));
  } else return true;
  tiles[next]?.focus();
  event.preventDefault();
  return true;
}

function PaletteSection({
  section,
  open,
  onToggle,
  library,
}: {
  section: Section;
  open: boolean;
  /** Absent while searching: every match is shown, and headings only name them. */
  onToggle?: () => void;
  library: WorkspaceLibrary;
}) {
  const gridId = useId();
  const headingRef = useRef<HTMLButtonElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  return (
    <section className="ws-palette-section" role="region" aria-label={section.label}>
      <h3 className="ws-palette-heading">
        <button
          ref={headingRef}
          type="button"
          className="ws-palette-toggle"
          aria-expanded={open}
          aria-controls={open ? gridId : undefined}
          disabled={!onToggle}
          onClick={onToggle}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" && open) {
              event.preventDefault();
              gridRef.current?.querySelector<HTMLButtonElement>(".ws-tile")?.focus();
            }
          }}
        >
          <span className="ws-palette-caret" aria-hidden="true">
            {open ? "▾" : "▸"}
          </span>
          <span className="ws-palette-label">{section.label}</span>
        </button>
      </h3>
      {open && (
        <div
          ref={gridRef}
          id={gridId}
          className="ws-tiles ws-tile-grid"
          onKeyDown={(event) => {
            if (!moveAmongTiles(event)) {
              event.preventDefault();
              headingRef.current?.focus();
            }
          }}
        >
          {section.items.map((item) => (
            <PaletteTile key={item.versionId} item={item} dragType={section.dragType} onPlace={section.place} library={library} />
          ))}
        </div>
      )}
    </section>
  );
}

export function Palette({
  library,
  loading,
  onPlace,
  onPlaceBlock,
  kind = "microfluidic",
}: {
  /** Which kind of design this is: the palette offers only its templates (D34). */
  kind?: DesignKind;
  library: WorkspaceLibrary;
  loading: boolean;
  onPlace: (versionId: string) => void;
  /** Absent where blocks cannot be placed, such as inside a block definition (D16). */
  onPlaceBlock?: (versionId: string) => void;
}) {
  const [search, setSearch] = useState("");
  // Null until this person opens or closes something: the first section starts open.
  const [openLabels, setOpenLabels] = useState<string[] | null>(loadPaletteSections);
  const [panel, setPanel] = useState<PalettePanel>(loadPalettePanel);
  const panelRef = useRef(panel);
  panelRef.current = panel;
  const keepPanel = (next: PalettePanel) => {
    setPanel(next);
    savePalettePanel(next);
  };

  const blocks = onPlaceBlock ? paletteBlocks(library, search) : [];
  const sections: Section[] = [
    ...paletteGroups(library, search, kind).map((group) => ({
      label: group.label,
      items: group.items,
      dragType: TEMPLATE_DRAG_TYPE,
      place: onPlace,
    })),
    ...(onPlaceBlock && blocks.length > 0
      ? [{ label: "Blocks", items: blocks, dragType: BLOCK_DRAG_TYPE, place: onPlaceBlock }]
      : []),
  ];
  const searching = search.trim().length > 0;
  const opened = openLabels ?? (sections[0] ? [sections[0].label] : []);

  const toggle = (label: string) => {
    const next = opened.includes(label) ? opened.filter((open) => open !== label) : [...opened, label];
    setOpenLabels(next);
    savePaletteSections(next);
  };

  if (!panel.open) {
    return (
      <aside className="ws-palette ws-palette-closed" aria-label="Component palette">
        <PanelTab side="left" open={false} name="palette" onToggle={() => keepPanel({ ...panel, open: true })} />
      </aside>
    );
  }

  return (
    <aside className="ws-palette" aria-label="Component palette" style={{ width: panel.width }}>
      <PanelTab side="left" open name="palette" onToggle={() => keepPanel({ ...panel, open: false })} />
      <div className="ws-palette-scroll">
      <input
        className="ws-search"
        type="search"
        placeholder="Search"
        aria-label="Search templates"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      {loading ? (
        <p className="ws-muted ws-small">Loading the library…</p>
      ) : sections.length === 0 ? (
        <p className="ws-muted ws-small">{searching ? "No templates match." : "The library has no templates."}</p>
      ) : (
        <div className="ws-palette-sections">
          {sections.map((section) => (
            <PaletteSection
              key={section.label}
              section={section}
              open={searching || opened.includes(section.label)}
              onToggle={searching ? undefined : () => toggle(section.label)}
              library={library}
            />
          ))}
        </div>
      )}
      </div>
      <PaneResizer
        label="Resize palette"
        width={panel.width}
        limits={PALETTE_WIDTH}
        edge="right"
        onResize={(width) => {
          // Kept in the ref at once, so saving straight after (a key press) sees it.
          panelRef.current = { ...panelRef.current, width };
          setPanel(panelRef.current);
        }}
        onDone={() => savePalettePanel(panelRef.current)}
      />
    </aside>
  );
}
